import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { BsFillClockFill } from 'react-icons/bs';
import { FaUser } from 'react-icons/fa';
import { Associate } from '../../../types/associates';
import { showFullName, showPicture } from '../../../utils/commonFunctions';
import AcademiaRating from '../AcademiaRating';
import CardBadge from '../Badges/CardBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import ReferralAvailableSlotModal from '../Modals/ReferralAvailableSlotModal';
import AssociateCardCount from './AssociateCardCount';

const ReferredAssociateCard = ({ associate }: { associate: Associate }) => {
  // destructuring the associate object here
  const {
    first_name,
    middle_name,
    last_name,
    profile_picture,
    username,
    total_appointments,
    total_languages,
    feedback_count,
    associate_rating,
    credits_per_appointments,
    slot_durations,
    language_names,
    service_name,
    service_id,
  } = associate || {};

  // integration of react hooks here
  const [showModal, setShowModal] = useState<boolean>(false);

  // rendering referred associate card component here
  return (
    <div className='bg-base-white p-4 xl:p-2 2xl:p-4 rounded-lg shadow-academia-sm'>
      <figure className='flex items-center justify-center'>
        {profile_picture ? (
          <div className='size-24 rounded-full overflow-hidden'>
            <img
              className='h-full w-full object-cover'
              src={showPicture(profile_picture)}
              alt={`avatar of ${username}`}
            />
          </div>
        ) : (
          <div className='flex items-center justify-center bg-base-light-gray size-24 rounded-full'>
            <FaUser className='size-14 text-gray-300' />
          </div>
        )}
      </figure>

      <figcaption className='grid grid-rows-[220px_90px] mt-3 space-y-2'>
        <div className='space-y-2'>
          <h3 className='text-center text-xl text-legal-dark-blue font-semibold'>
            {showFullName(first_name, middle_name, last_name)}
          </h3>

          <p className='text-center text-xl font-medium text-base-dark-green'>
            {credits_per_appointments} credits
            <span className='text-sm font-normal text-base-orange'>/slot</span>
          </p>

          <div className='flex items-center justify-center !mt-1'>
            <div className='flex items-center gap-1 text-base-dark-green font-semibold text-sm border-2 border-base-dark-green rounded-full w-fit px-3 bg-base-light-green/30'>
              <BsFillClockFill />
              <p>Slot: {slot_durations} Minutes</p>
            </div>
          </div>

          <div className='flex items-center justify-center'>
            <AcademiaRating
              size={30}
              ratingValue={associate_rating}
              isReadonly
            />
          </div>

          <div className='grid grid-cols-3'>
            <AssociateCardCount
              count={total_appointments}
              text='Appointments'
            />
            <AssociateCardCount count={feedback_count} text='Reviews' />
            <AssociateCardCount count={total_languages} text='Languages' />
          </div>

          <div className='flex items-center gap-2 !mt-4'>
            {language_names.map((language, index) =>
              language ? <CardBadge key={index} badgeText={language} /> : null
            )}
          </div>
        </div>

        <div className='!mt-3 space-y-1'>
          <p className='text-center md:text-left text-legal-dark-blue'>
            Referred for{' '}
            <span className='font-semibold text-base-orange'>
              {service_name}
            </span>
          </p>

          <AcademiaButton
            type='button'
            onClick={() => setShowModal(true)}
            fullWidth
            additionalClassNames='!py-2'
          >
            View Available Slots
          </AcademiaButton>
        </div>
      </figcaption>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showModal && (
          <ReferralAvailableSlotModal
            associate={associate}
            serviceId={service_id}
            handleModalClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReferredAssociateCard;
